import { useWallet } from '@txnlab/use-wallet';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import config from '../config';
import { useTraitSwapFinalizeMutation, useTraitSwapInitiateMutation } from '../hooks/useTraitSwap';
import useTraitSwapStore from '../hooks/useTraitSwapStore';
import BalanceAlgo from './BalanceAlgo';
import BalanceTreats from './BalanceTreats';
import Button from './Button';
import FloatingActions from './FloatingActions';
import FullScreenLoader from './FullScreenLoader';
import NftImage from './NftImage';

const TraitSwapFloatingActions = () => {
  const { activeAddress } = useWallet();
  const {
    activeTab,
    setActiveTab,
    selectedNfts,
    swappedLayers,
    failedTraitImages,
    reset,
    initiateSwapSuccess,
    setDismissedFlaggedTraits,
    dismissedFlaggedTraits,
  } = useTraitSwapStore();
  const { mutate: initiateSwap, isLoading } = useTraitSwapInitiateMutation();
  const { mutate: finalizeSwap, isLoading: finalizeLoading } = useTraitSwapFinalizeMutation();

  const finalizeIsEnabled = selectedNfts.length === config.traitSwapMaxNfts && swappedLayers.length > 0 && failedTraitImages.length === 0;

  const allTraits = selectedNfts.map((nft) => Object.entries(nft?.metadata?.properties ?? {})).flat();
  const swappedTraits = swappedLayers.map((layer) => allTraits.filter((trait) => trait[0] === layer)).flat();
  const flaggedTraits = swappedTraits.filter((trait) => config.flaggableTraits.includes(trait[1]));
  const flaggedWithMultipleOptions = allTraits.filter((trait) => config.traitsWithMultipleOptions.includes(trait[1]));

  const hasSpecialTrait = allTraits.some((trait) => trait[0] === 'Special' && trait[1] !== 'None');
  const hasBannedTrait = allTraits.some((trait) => config.bannedTraits.includes(trait[1]));
  const bannedTraits = allTraits.filter((trait) => config.bannedTraits.includes(trait[1]));

  const firstKittyTraits = selectedNfts?.[0]?.metadata?.properties;
  const secondKittyTraits = selectedNfts?.[1]?.metadata?.properties;

  const firstLitterSwappedTraits = swappedTraits.filter((trait) => swappedTraits.indexOf(trait) % 2 === 1);
  const secondLitterSwappedTraits = swappedTraits.filter((trait) => swappedTraits.indexOf(trait) % 2 === 0);

  const updatedFirstKittyTraits = { ...firstKittyTraits };
  const updatedSecondKittyTraits = { ...secondKittyTraits };

  firstLitterSwappedTraits.forEach((trait) => {
    updatedFirstKittyTraits[trait[0]] = trait[1];
  });

  secondLitterSwappedTraits.forEach((trait) => {
    updatedSecondKittyTraits[trait[0]] = trait[1];
  });

  const requiredOtherTraits = config.traitsThatRequireOtherTraits.filter((t) => swappedTraits.some((st) => st[1] === t.trait));
  const kittyWithRequiredTraits = requiredOtherTraits
    .map((t) => (updatedFirstKittyTraits[t.traitLayer] === t.trait ? 'first' : updatedSecondKittyTraits[t.traitLayer] === t.trait ? 'second' : null))
    .includes('first')
    ? updatedFirstKittyTraits
    : updatedSecondKittyTraits;

  const kittyWithRequiredTraitsHasCorrectTraits = requiredOtherTraits.every(
    (t) => kittyWithRequiredTraits[t.requiredTraitLayer] === t.requiredTraitValue
  );

  const onBack = () => {
    setActiveTab(0);
  };

  const onNext = () => {
    setActiveTab(1);
  };

  const onInitiate = () => {
    if (!activeAddress) {
      toast.error('Please connect your wallet');
      return;
    }

    initiateSwap({
      address: activeAddress,
      selectedNfts: selectedNfts.map((nft) => nft.id),
      swappedLayers: swappedLayers,
    });
  };

  const onFinalize = () => {
    if (!activeAddress) {
      toast.error('Please connect your wallet');
      return;
    }

    finalizeSwap({
      address: activeAddress,
      selectedNfts: selectedNfts.map((nft) => nft.id),
      swappedLayers: swappedLayers,
    });
  };

  const onSubmit = () => {
    if (initiateSwapSuccess) {
      onFinalize();
    } else {
      onInitiate();
    }
  };

  return (
    <>
      <FullScreenLoader loading={isLoading || finalizeLoading} label='Loading...' />
      <FloatingActions visible={selectedNfts.length > 0}>
        <div className='flex items-center flex-col md:flex-row justify-between gap-5 relative z-20'>
          <div className='flex items-center gap-6'>
            <div className='flex gap-4'>
              <div className='relative relative h-[60px] w-[60px] rounded-md bg-gray-400'>
                <div className='absolute left-[-8px] top-[-8px] z-10 z-30 flex h-[24px] w-[24px] items-center justify-center rounded-full border-2 border-white bg-primary-500'>
                  <p className='text-xs font-bold text-white'>{1}</p>
                </div>
                {selectedNfts[0] && <NftImage nft={selectedNfts[0]} className='absolute inset-0 rounded-md' />}
              </div>
              <div className='relative relative h-[60px] w-[60px] rounded-md bg-gray-400'>
                <div className='absolute left-[-8px] top-[-8px] z-10 z-30 flex h-[24px] w-[24px] items-center justify-center rounded-full border-2 border-white bg-primary-500'>
                  <p className='text-xs font-bold text-white'>{2}</p>
                </div>
                {selectedNfts[1] && <NftImage nft={selectedNfts[1]} className='absolute inset-0 rounded-md' />}
              </div>
            </div>

            {selectedNfts.length === config.traitSwapMaxNfts && (
              <div className='flex flex-col'>
                <span className='font-medium text-gray-300'>Swaps</span>
                <div className='flex items-center gap-2'>
                  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512' className={clsx('w-4 h-4 text-current text-gray-400')}>
                    <path
                      d='M103 497c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-55-55L424 408c13.3 0 24-10.7 24-24s-10.7-24-24-24L81.9 360l55-55c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L7 367c-9.4 9.4-9.4 24.6 0 33.9l96 96zM441 145c9.4-9.4 9.4-24.6 0-33.9L345 15c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l55 55L24 104c-13.3 0-24 10.7-24 24s10.7 24 24 24l342.1 0-55 55c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l96-96z'
                      fill='currentColor'
                    />
                  </svg>
                  {swappedLayers.length}/{config.traitSwapMaxLayers}
                </div>
              </div>
            )}

            {selectedNfts.length === config.traitSwapMaxNfts && (
              <div className='flex flex-col text-lg'>
                <span className='font-medium text-gray-300'>Cost</span>
                <div className='flex gap-3'>
                  <BalanceTreats value={config.traitSwapTreatsCost} />
                  <BalanceAlgo value={config.traitSwapAlgoCost} />
                </div>
              </div>
            )}
          </div>

          <div>
            {activeTab === 0 && (
              <div className='flex gap-4'>
                <Button theme='white' onClick={reset}>
                  Reset
                </Button>
                <Button disabled={selectedNfts.length < config.traitSwapMaxNfts} onClick={onNext}>
                  Next
                </Button>
              </div>
            )}
            {activeTab === 1 && (
              <div className='flex gap-4'>
                <Button theme='white' disabled={selectedNfts.length < config.traitSwapMaxNfts} onClick={onBack}>
                  Back
                </Button>
                <Button disabled={!finalizeIsEnabled} loading={isLoading || finalizeLoading} onClick={onSubmit}>
                  {initiateSwapSuccess ? 'Finalize' : 'Initiate'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {failedTraitImages.length > 0 && (
          <div className='absolute inset-0 flex w-full items-center justify-between md:rounded-full bg-red-500 px-8 z-50'>
            <div className='max-w-[300px]'>
              <p className='text-lg leading-tight text-gray-100'>Some trait images failed to load. Please contact support.</p>
            </div>

            <Button theme='white' onClick={reset}>
              Reset
            </Button>
          </div>
        )}

        {flaggedTraits.length > 0 && !dismissedFlaggedTraits && (
          <div className='absolute inset-0 z-30 flex w-full flex-col md:flex-row items-center justify-center gap-4 md:justify-between bg-red-500 px-8 md:rounded-full'>
            <div className='max-w-[350px]'>
              <p className='text-lg leading-tight text-gray-100'>
                WAIT: The {flaggedTraits.map((trait) => trait[1]).join(', ')} {flaggedTraits.length > 1 ? 'traits have' : 'trait has'} a high chance
                of clashing. Look over your kitty and ensure there are no clashes!
              </p>
            </div>
            <div className='flex gap-4'>
              <Button theme='outline-white' onClick={reset}>
                Reset
              </Button>

              <Button theme='outline-white' onClick={() => setDismissedFlaggedTraits(true)}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {flaggedWithMultipleOptions.length > 0 && !dismissedFlaggedTraits && (
          <div className='absolute inset-0 z-30 flex w-full flex-col md:flex-row items-center justify-center gap-4 md:justify-between bg-red-500 px-8 md:rounded-full'>
            <div className='max-w-[350px]'>
              <p className='text-lg leading-tight text-gray-100'>
                WAIT: The {flaggedWithMultipleOptions.map((trait) => trait[1]).join(', ')}{' '}
                {flaggedWithMultipleOptions.length > 1 ? 'traits have' : 'trait has'} multiple styles. You may lose the original style.
              </p>
            </div>
            <div className='flex gap-4'>
              <Button theme='outline-white' onClick={reset}>
                Reset
              </Button>

              <Button theme='outline-white' onClick={() => setDismissedFlaggedTraits(true)}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {hasSpecialTrait && (
          <div className='absolute inset-0 z-30 flex w-full flex-col md:flex-row items-center justify-center gap-4 md:justify-between bg-red-500 px-8 md:rounded-full'>
            <div className='max-w-[400px]'>
              <p className='text-lg leading-tight text-gray-100'>WAIT: This kitty has a Special trait. You cannot swap traits with this kitty!</p>
            </div>
            <div className='flex gap-4'>
              <Button theme='outline-white' onClick={reset}>
                Reset
              </Button>
            </div>
          </div>
        )}

        {hasBannedTrait && (
          <div className='absolute inset-0 z-30 flex w-full flex-col md:flex-row items-center justify-center gap-4 md:justify-between bg-red-500 px-8 md:rounded-full'>
            <div className='max-w-[500px]'>
              <p className='text-lg leading-tight text-gray-100'>
                WAIT: The {bannedTraits.map((trait) => trait[1]).join(', ')} {bannedTraits.length > 1 ? 'traits' : 'trait'} cannot be swapped.
              </p>
            </div>
            <div className='flex gap-4'>
              <Button theme='outline-white' onClick={reset}>
                Reset
              </Button>
            </div>
          </div>
        )}

        {requiredOtherTraits.length > 0 && !kittyWithRequiredTraitsHasCorrectTraits && (
          <div className='absolute inset-0 z-30 flex w-full flex-col md:flex-row items-center justify-center gap-4 md:justify-between bg-red-500 px-8 md:rounded-full'>
            <div className='max-w-[500px]'>
              <p className='text-lg leading-tight text-gray-100'>
                WAIT: The {requiredOtherTraits.map((trait) => trait.trait).join(', ')} requires{' '}
                {requiredOtherTraits.map((trait) => `${trait.requiredTraitValue} ${trait.requiredTraitLayer}`).join(', ')}. Please swap the required
                traits first.
              </p>
            </div>
            <div className='flex gap-4'>
              <Button theme='outline-white' onClick={reset}>
                Reset
              </Button>
            </div>
          </div>
        )}
      </FloatingActions>
    </>
  );
};

export default TraitSwapFloatingActions;
